/**
 * Thin fetch wrapper. Relative URLs + same-origin cookies so it works
 * under any nginx prefix. All endpoints return JSON except GETs for PDF.
 */

export type UserRole = "user" | "admin" | "developer";

export interface AuthUser {
  id: string;
  email: string;
  displayName: string;
  role: UserRole;
}

export interface DayHours {
  mo?: number; di?: number; mi?: number; do?: number; fr?: number; sa?: number;
}

export interface WorkSchedule {
  pattern?: "weekly" | "biweekly";
  week_a?: DayHours;
  week_b?: DayHours | null;
  soll_h_woche_a?: number;
  soll_h_woche_b?: number | null;
  notes?: string | null;
  imported_from?: string | null;
  imported_at?: string | null;
}

export type UeberstundenPayMode = "cash" | "za" | "pauschale";

export interface Employee {
  id: string;
  client_id: string;
  dienstnehmernummer: string;
  first_name: string;
  last_name: string;
  active: boolean;
  work_schedule: WorkSchedule | null;
  /** Wie 50%-Überstunden (>42h/Woche) ausbezahlt werden. Default 'za'. */
  ueberstunden_pay_mode: UeberstundenPayMode;
  /** Karte 725 — Überstundenpauschale laut Vertrag; `null` = nicht hinterlegt
   *  (Zulagenblatt-Karten mit Überstunden halten dann an). */
  ueberstunden_pauschaliert: boolean | null;
  current_kv_id?: string | null;
  current_kv_name?: string | null;
  weekly_ueberstunden_threshold_h?: number | null;
  weekly_threshold_from_month?: string | null;
  weekly_threshold_revision_id?: string | null;
}

export interface Client {
  id: string;
  name: string;
  contact_email: string | null;
  contact_name: string | null;
  notes: string | null;
  active: boolean;
}

export interface ClientKvDefault {
  id: string;
  client_id: string;
  kv_config_id: string;
  valid_from: string;
  valid_until: string | null;
  note: string | null;
}

export interface EmployeeKvAssignment {
  id: string;
  employee_id: string;
  kv_config_id: string;
  valid_from: string;
  valid_until: string | null;
  note: string | null;
}

export interface KvConfig {
  id: string;
  client_id: string;
  name: string;
  normal_hours_per_week: string;
  normal_hours_per_day: string;
  overtime_50_start: string | null;
  overtime_50_end: string | null;
  overtime_100_applies_sunday: boolean;
  overtime_100_applies_holiday: boolean;
  zeitausgleich_factor_50: string;
  zeitausgleich_factor_100: string;
  pause_minutes_after_6h: number;
  is_default: boolean;
}

export interface CrossMonthInfo {
  /** "no_split"            = Tag 1 ist Mo → keine geteilte Woche, kein Hinweis
   *  "ok"                  = Vormonats-Karte ist im Calc-Snapshot abgebildet
   *  "missing_prev_data"   = Vormonats-Karte fehlt → Mehrstunden in KW1 incomplete
   *  "prev_data_drift"     = Vormonat hat sich seit unserem Calc geändert/dazukommen
   */
  state: "no_split" | "ok" | "missing_prev_data" | "prev_data_drift";
  first_week_iso: number | null;
  prev_card: {
    id: string;
    state: string;
    updated_at: string;
    month: string;
  } | null;
  snapshot: {
    prev_card_id: string | null;
    prev_card_updated_at: string | null;
    prev_card_state: string | null;
  } | null;
}

export interface Timesheet {
  id: string;
  client_id: string;
  employee_id: string | null;
  month: string;
  state: string;
  pdf_orig_filename: string;
  pdf_bytes: number;
  data: Record<string, DayEntry[]>;
  created_at: string;
  updated_at: string;
  /** Live-State (2026-05-22) der "geteilten Woche über Monatswechsel" — wird
   *  vom GET /api/timesheets/:id mit jedem Read frisch berechnet, nicht
   *  persistiert. Frontend rendert dynamischen Banner basierend auf state. */
  cross_month_info?: CrossMonthInfo;
  calc_stale?: boolean;
  weekly_threshold_stale?: boolean;
  effective_weekly_threshold?: { hours: number; source: "mandant" | "employee"; revision_id: string | null; from_month: string | null } | null;
}

export interface Card extends Timesheet {
  first_name: string | null;
  last_name: string | null;
  dienstnehmernummer: string | null;
  /** 'excel_import' (T13/T14, Migration 041): 3g-Excel-Report als expenses-
   *  Karte OHNE PDF (pdf_path leer) — Detail rendert Summen-/Roh-Tabellen. */
  kind: "timesheet" | "expense" | "unknown" | "excel_import";
  /** pg-boss retry_count for the active OCR job, 0 if no retries yet. */
  ocr_retry_count?: number;
  /** pg-boss job state: 'active' | 'retry' | 'created' or null. */
  ocr_queue_state?: "active" | "retry" | "created" | null;
  /** pg-boss job.id of the currently-active OCR run (Run-UUID, Glass-Box Schicht B). */
  ocr_queue_job_id?: string | null;
  /** ISO timestamp when the active OCR job was enqueued. */
  ocr_queued_at?: string | null;
  /** ISO timestamp when the worker started processing (null while in queue). */
  ocr_started_at?: string | null;
  /** Number of OCR jobs created BEFORE this one and still waiting. */
  ocr_queue_position?: number | null;
  /** Server-computed: state='ocr_running' UND updated_at > 15min alt. Heißt
   *  pg-boss-Handler ist gestorben ohne state-Recycling. UI zeigt Banner +
   *  Restart-Button wenn true. */
  stuck_ocr?: boolean;
  /** Minutes seit letztem updated_at während state=ocr_running. null wenn nicht
   *  in ocr_running. Float — UI rundet auf Ganze. */
  stuck_minutes?: number | null;
  /** Server-computed: card hat eine calculation aber Settings haben sich
   *  seit der Berechnung geändert. Export würde stale Werte nutzen — UI
   *  zeigt Banner "Recalc nötig". Wird in /api/cards/by-id berechnet. */
  calc_stale?: boolean;
  weekly_threshold_stale?: boolean;
  effective_weekly_threshold?: { hours: number; source: "mandant" | "employee"; revision_id: string | null; from_month: string | null } | null;
  /** Server-computed: Karte hat Mitarbeiter aber dieser hat keine
   *  work_schedule konfiguriert. Validieren würde 400 zurückgeben (Hub-Endpoint
   *  blockt das). Kanban zeigt gelbes Warn-Dreieck damit Birgit weiß: erst
   *  Mitarbeiter-Sollzeiten setzen. (2026-05-20, Anlassfall Mario Haider 0313d751) */
  missing_schedule?: boolean;
}

/** Kanban-Filter-Wert. Die Sonder-Filter "all"/"unknown"/"conflict" plus die
 *  kind-Aliase "timesheet"/"expense" (Legacy-Contract), ODER ein doc_type-Slug
 *  (card #523: Tabs sind doc_type-getrieben, nicht mehr ein fixes Enum). Das
 *  `(string & {})`-Glied lässt beliebige doc_type-ids zu, ohne die bekannten
 *  Literale aus der Autocompletion zu verlieren. */
export type CardTypeFilter =
  | "all" | "timesheet" | "expense" | "unknown" | "conflict"
  | (string & {});

/** T13/T14: eine angereicherte Zeile des 3g-Excel-Imports
 *  (expenses.data.rows, Vertrag: hub/src/lib/xlsx-import.ts ImportZeile).
 *  lohnart ist laut Hub-Typ string|null, die Kunden-Matrix führt aber
 *  NUMERISCHE Lohnarten (11, 13, …) — deshalb hier string|number|null,
 *  UI rendert via String(). */
export interface ExcelImportRow {
  mitarbeiter_id: string;
  dn: string;
  /** Original-Schreibweise aus dem Excel. */
  art: string;
  /** kanonische Schreibweise laut Übersetzungsmatrix. */
  excel_art: string;
  /** RZL-Lohnart laut Matrix; null = nicht exportierbar. */
  lohnart: string | number | null;
  datum: string;
  einheit: string;
  ep: number;
  anzahl: number;
  kosten: number;
  projekt: string;
  kostenstelle: string;
}

/** T13/T14: Summenzeile je (Mitarbeiter × Lohnart) des Excel-Imports
 *  (expenses.data.summen, Vertrag: hub/src/lib/xlsx-import.ts SummenZeile). */
export interface ExcelImportSumme {
  mitarbeiter_id: string;
  dn: string;
  lohnart: string | number | null;
  /** Excel-Arten, die in diese Gruppe geflossen sind (dedupliziert). */
  excel_arten: string[];
  anzahl: number;
  kosten: number;
  zeilen: number;
}

/** T13/T14: expenses.data einer Excel-Import-Karte (kind='excel_import',
 *  state='validated', pdf_path LEER — kein PDF-Panel rendern). */
export interface ExcelImportData {
  source: "excel";
  filename: string;
  rows: ExcelImportRow[];
  summen: ExcelImportSumme[];
  /** Excel-Arten mit lohnart=null — importiert, aber im RZL-Export nicht
   *  abbildbar; UI kennzeichnet sie als gelben Hinweis. */
  nicht_exportierbar: string[];
  /** T24b: Marker + Meta der gespeicherten Original-xlsx. Wenn stored=true,
   *  liefert GET /api/expenses/:id/source die Datei 1:1 (Download-Link). Karten
   *  von vor T24b haben das Feld nicht → kein Link. */
  source_file?: {
    stored: boolean;
    ext: string;
    filename: string;
    content_type: string;
    bytes: number;
  };
}

export interface CardsResponse {
  cards: Card[];
  max_event_id: string;
  changed_ids: string[];
  /** Backend-Signal: nichts seit `since` geändert — Frontend behält die
   *  existierende Liste (kein setCards). Spart Re-Render + Re-Sort. */
  unchanged?: boolean;
}

// Stuck-OCR Diagnose (2026-05-21)
export type StuckHypothesis = "running_normal" | "running_long" | "sidecar_silent" | "worker_crashed" | "no_job" | "not_stuck";
export interface StuckDiagnosis {
  card_id: string;
  kind: string;
  card_state: string;
  stuck_minutes: number | null;
  updated_at: string;
  active_variant: string | null;
  job: {
    job_id: string;
    job_state: string;
    retry_count: number;
    created_on: string;
    started_on: string | null;
    completed_on: string | null;
    job_age_s: number;
    error_msg: string | null;
  } | null;
  sidecar: {
    active?: {
      request_id: string;
      op_label: string;
      started_at: number;
      last_detail: string;
      elapsed_total_s: number;
      elapsed_since_heartbeat_s: number;
      hard_timeout_s: number;
    } | null;
    error?: string;
  } | null;
  hypothesis: StuckHypothesis;
  advice: string;
}

// Image-Preprocessing-Lab — Phase 1 Step 3 (2026-05-21)
export interface AnchorMeasure {
  anchors_found: number;
  match_ratio: number;
  index_shifted: boolean;
  form_type: string;
  grid_rows: number;
  image_size?: [number, number];
  anchor_top_pct?: number | null;
  anchor_bot_pct?: number | null;
  days_detected?: number[];
  error?: string;
}
export interface PreprocessVariant {
  id: string;
  type: "clahe" | "sauvola" | "hough_redraw" | string;
  params: Record<string, unknown>;
  measure: AnchorMeasure;
  created_at: string;
  created_by: string;
}

export interface DayEntry {
  name: "von1" | "bis1" | "von2" | "bis2" | "symb";
  value: string;
}

/** Phase-2 OCR cell shape: data.days[day].<field> = OcrDayCell.
 * `klasse` is set when the validator pass ran (OCR_VALIDATOR_ENABLED=1). */
export interface OcrDayCell {
  value: string | null;
  confidence?: number;
  klasse?: "1" | "2" | "3" | "4" | "5a" | "5b";
  audit?: Record<string, unknown>;
  /** Source/raw fields kept by the engine for traceability. */
  source?: string;
  raw_text?: string;
}

export interface User {
  id: string;
  email: string;
  display_name: string;
  role: UserRole;
  created_at: string;
  updated_at: string;
}

// Test-protocols (hub/src/routes/test-protocols.ts) — single-card highlight run.
// The 5-signal verdict + reasons mirror runHighlightTest exactly (refactor 2026-05-18).
export type HighlightVerdictStatus = "VALID" | "REJECTED" | "NO_GRID" | "FETCH_ERROR";
export type HighlightRejectReason =
  | "anchor_index_shift"
  | "cols_incomplete"
  | "cv_high"
  | "row_count"
  | "homography_weak"
  | "bounds_implausible"
  | "anchor_mismatch"
  | "page_skewed"
  | "insufficient_anchor_coverage"
  | "form_type_unknown"
  // Diäten-Validator (Gridlinien-Truth statt Tag-Nummern-Anchor, 2026-06-09):
  | "spatial_missing"
  | "gridline_mismatch"
  | "gridline_coverage_low";

export interface HighlightCardResult {
  id: string;
  pdf_orig_filename: string | null;
  card_kind: "timesheet" | "unknown" | "expense";
  status: HighlightVerdictStatus;
  cv: number | null;
  rows_count: number;
  inlier_ratio: number | null;
  top_pct: number | null;
  bottom_pct: number | null;
  anchor_top_pct: number | null;
  anchor_bot_pct: number | null;
  anchors_found: number;
  /** Page rotation in degrees (signed). Null when grid-detect couldn't
   * isolate enough long horizontal lines for a stable Hough estimate. */
  skew_angle_deg: number | null;
  /** Welche Spalten der Grid-Detector erkannt hat. Vollständige Stundenliste
   *  hat 5 (von1/bis1/von2/bis2/symb). Fehlende Spalten → cols_incomplete. */
  cols_detected: string[];
  reject_reasons?: HighlightRejectReason[];
  error?: string;
}

export interface HighlightSkipped {
  card_id: string;
  card_kind: "timesheet" | "unknown";
  pdf_orig_filename: string | null;
  reason: string;
  detail?: string;
}

export type RotationVerdict =
  | "ROTATION_OK"
  | "NEEDS_ROTATION"
  | "LOW_CONFIDENCE"
  | "OSD_ERROR"
  | "PDF_FILE_MISSING";

export interface RunRotationForCardResponse {
  /** test_run_protocols UUID — Single-Card-Rotation-Lauf, persistiert. */
  id: string;
  card_id: string;
  verdict: RotationVerdict;
  /** Erkannte Drehung — 0/90/180/270. */
  rotate: 0 | 90 | 180 | 270;
  /** OSD-Confidence normalisiert 0..1 (raw / 3.0 clamped). */
  confidence: number;
  /** Raw Tesseract orientation_conf — hilft beim Tuning. */
  raw_conf: number;
  /** Tesseract-Script-Detection (Latin, Japanese, …). */
  script: string;
  /** Confidence-Schwelle, ab der Verdict nicht mehr LOW_CONFIDENCE ist. */
  confidence_threshold: number;
  /** Set vom Latest-Endpoint, nicht vom POST. ISO. */
  run_at?: string;
  /** Detail bei OSD_ERROR / PDF_FILE_MISSING. */
  error?: string;
}

export interface RunHighlightForCardResponse {
  /** test_run_protocols UUID — kann dem Agent referenziert werden. */
  id: string;
  card_id: string;
  /** Top-level verdict shortcut. = card_result.status OR skipped.reason OR "UNKNOWN". */
  verdict: HighlightVerdictStatus | string;
  reasons: HighlightRejectReason[];
  card_result: HighlightCardResult | null;
  skipped: HighlightSkipped | null;
  /** Set by the latest-endpoint (persistierter Lauf), nicht vom POST. ISO timestamp. */
  run_at?: string;
}

// Capture the SPA's base URL at module load (before any React Router navigation).
// document.baseURI changes with pushState, so we need a stable snapshot.
const INITIAL_BASE = document.baseURI;

const ERROR_MAP: Record<string, string> = {
  invalid_credentials: "E-Mail oder Passwort falsch.",
  not_found: "Eintrag nicht gefunden.",
  user_exists: "Diese E-Mail ist bereits vergeben.",
  email_exists: "Diese E-Mail ist bereits vergeben.",
  "id exists": "Dieser Slug ist bereits vergeben.",
  "name exists": "Dieser Name ist bereits vergeben.",
  "valid_from collision": "An diesem Stichtag existiert bereits ein Eintrag.",
  "valid_until must be >= valid_from": "Gültig-bis muss nach Gültig-ab liegen.",
  "cannot delete yourself": "Du kannst dich nicht selbst löschen.",
  "cannot delete last admin": "Der letzte Admin kann nicht gelöscht werden.",
  "cannot downgrade last admin": "Der letzte Admin kann nicht degradiert werden.",
  "client not found": "Mandant nicht gefunden.",
  "client or kv_config not found": "Mandant oder KV-Konfiguration nicht gefunden.",
  "employee or kv_config not found": "Mitarbeiter oder KV-Konfiguration nicht gefunden.",
  "no changes": "Keine Änderungen eingegeben.",
  "csv file required": "CSV-Datei fehlt.",
  "csv parse failed": "CSV konnte nicht gelesen werden.",
  "pdf file required": "PDF-Datei fehlt.",
  "empty file": "Die Datei ist leer.",
  "pdf too large (max 10 MB)": "PDF ist zu groß (max 10 MB).",
  "month required": "Monat fehlt.",
  "invalid month (YYYY-MM)": "Ungültiger Monat (Format YYYY-MM).",
  "timesheet already exists for this employee + month":
    "Für diesen Mitarbeiter und Monat existiert bereits ein Timesheet.",
  "client_id required": "Mandant fehlt.",
  "employee_id required": "Mitarbeiter fehlt.",
  "employee_id and date required": "Mitarbeiter und Datum sind pflicht.",
  "date must be YYYY-MM-DD": "Datum muss im Format YYYY-MM-DD sein.",
  "employee not assigned": "Kein Mitarbeiter zugeordnet.",
  auto_detect_disabled:
    "Für diesen Mandanten muss der Dokumenttyp beim Hochladen gewählt werden. " +
    "Ist die Auswahl leer, sind für den Mandanten noch keine Dokumenttypen hinterlegt.",
};

const FIELD_LABELS: Record<string, string> = {
  email: "E-Mail",
  password: "Passwort",
  display_name: "Anzeigename",
  name: "Name",
  first_name: "Vorname",
  last_name: "Nachname",
  dienstnehmernummer: "Dienstnehmernummer",
  client_id: "Mandant",
  kv_config_id: "KV-Konfiguration",
  employee_id: "Mitarbeiter",
  valid_from: "Gültig ab",
  valid_until: "Gültig bis",
  contact_email: "Kontakt-E-Mail",
  contact_name: "Kontakt-Name",
  month: "Monat",
  id: "Slug",
  role: "Rolle",
  note: "Notiz",
};

function translateZodIssue(issue: any): string {
  const path = Array.isArray(issue.path) ? issue.path.join(".") : "";
  const field = FIELD_LABELS[path] ?? path ?? "Feld";
  if (issue.validation === "email") return `${field}: keine gültige E-Mail-Adresse.`;
  if (issue.code === "too_small") {
    if (issue.type === "string") {
      const min = issue.minimum;
      if (min === 1) return `${field} darf nicht leer sein.`;
      return `${field}: mindestens ${min} Zeichen.`;
    }
    if (issue.type === "number") return `${field}: muss mindestens ${issue.minimum} sein.`;
  }
  if (issue.code === "invalid_type") {
    if (issue.received === "undefined") return `${field} fehlt.`;
    return `${field}: falscher Datentyp.`;
  }
  if (issue.code === "invalid_string" && issue.validation === "regex") {
    return `${field}: ungültiges Format.`;
  }
  if (issue.code === "invalid_enum_value") return `${field}: ungültiger Wert.`;
  return `${field}: ${issue.message ?? "ungültig"}.`;
}

const HTTP_STATUS_DE: Record<number, string> = {
  400: "Ungültige Anfrage",
  401: "Nicht angemeldet — bitte neu einloggen.",
  403: "Zugriff verweigert.",
  404: "Nicht gefunden.",
  409: "Konflikt — der Vorgang kann nicht durchgeführt werden.",
  413: "Datei ist zu groß.",
  429: "Zu viele Anfragen — bitte kurz warten.",
  500: "Interner Serverfehler. Bitte versuchen Sie es erneut.",
  502: "Der Server ist vorübergehend nicht erreichbar. Bitte versuchen Sie es in einer Minute erneut.",
  503: "Der Server wird gewartet. Bitte versuchen Sie es in einer Minute erneut.",
  504: "Zeitüberschreitung. Bitte versuchen Sie es erneut.",
};

function translateApiError(status: number, bodyText: string): string {
  if (!bodyText || bodyText.trimStart().startsWith("<")) {
    const label = HTTP_STATUS_DE[status];
    return label ? `${label} (Fehlercode ${status})` : `Serverfehler (Fehlercode ${status}).`;
  }
  let parsed: any;
  try {
    parsed = JSON.parse(bodyText);
  } catch {
    const label = HTTP_STATUS_DE[status];
    return label ? `${label} (Fehlercode ${status})` : `Serverfehler (Fehlercode ${status}).`;
  }
  const err = parsed?.error;
  if (err && Array.isArray(parsed?.issues)) {
    return parsed.issues.map(translateZodIssue).join(" ");
  }
  if (typeof err === "string" && ERROR_MAP[err]) return ERROR_MAP[err]!;
  // Anlassfall 2026-08-11 (Franka, „auto_detect_disabled"): der Hub liefert zu
  // seinen Fehlern ein deutsches `message`-Feld mit — das wurde nie gelesen, und
  // der Kunde sah den rohen Maschinen-Code. Reihenfolge bewusst: kuratierte Map
  // zuerst (sie darf die Server-Formulierung uebersteuern), dann die
  // Server-Botschaft, erst danach der Code als letzte Notloesung.
  if (typeof parsed?.message === "string" && parsed.message.trim()) return parsed.message;
  if (typeof err === "string") return err;
  const label = HTTP_STATUS_DE[status];
  return label ? `${label} (Fehlercode ${status})` : `Serverfehler (Fehlercode ${status}).`;
}

export class ApiError extends Error {
  status: number;
  body: any;
  constructor(status: number, message: string, body: any) {
    super(message);
    this.status = status;
    this.body = body;
  }
}

async function req<T>(path: string, init: RequestInit = {}): Promise<T> {
  const url = new URL(path, INITIAL_BASE).toString();
  const res = await fetch(url, {
    credentials: "same-origin",
    headers: init.body instanceof FormData ? init.headers : { "content-type": "application/json", ...(init.headers ?? {}) },
    ...init,
  });
  if (!res.ok) {
    const text = await res.text().catch(() => "");
    let parsed: any = null;
    try { parsed = JSON.parse(text); } catch { /* not JSON */ }
    throw new ApiError(res.status, translateApiError(res.status, text), parsed);
  }
  return (await res.json()) as T;
}

export const api = {
  async me(): Promise<AuthUser> {
    const body = await req<{ user: AuthUser }>("api/me");
    return body.user;
  },
  /** T04: EINZIGE Regelwerk-Quelle fürs Frontend — Hub liefert dieselbe
   *  loadClientRuleset-Sicht, die der Worker rechnet (kein Anzeige-Drift).
   *  404 no_ruleset (fail-loud, Mandant ohne client_rules-Row) → null. */
  async clientRules(clientId: string | null | undefined): Promise<
    { ruleset: import("./client-rules").ClientRuleset; has_db_row: boolean } | null
  > {
    try {
      return await req(`api/client-rules?client_id=${encodeURIComponent(clientId || "default")}`);
    } catch (e) {
      if (e instanceof ApiError && e.status === 404) return null;
      throw e;
    }
  },
  async login(email: string, password: string): Promise<AuthUser> {
    const body = await req<{ user: AuthUser }>("api/auth/login", {
      method: "POST",
      body: JSON.stringify({ email, password }),
    });
    return body.user;
  },
  async logout(): Promise<void> {
    await req<{ ok: boolean }>("api/auth/logout", { method: "POST" });
  },

  // Admin (monthly summary + re-OCR + feedback-CSV, admin-only)
  admin: {
    async monthSummary(month: string | null, clientId?: string): Promise<any> {
      const p = new URLSearchParams();
      if (month) p.set("month", month);
      if (clientId) p.set("client_id", clientId);
      return req<any>(`api/admin/month-summary?${p}`);
    },
    async reOcr(clientId?: string, month?: string): Promise<{ requeued_timesheets: number; requeued_expenses: number }> {
      const body: any = {};
      if (clientId) body.client_id = clientId;
      if (month) body.month = month;
      return req<any>("api/admin/re-ocr", { method: "POST", body: JSON.stringify(body) });
    },
    feedbackCsvUrl(month: string | null, clientId?: string): string {
      const p = new URLSearchParams();
      if (month) p.set("month", month);
      if (clientId) p.set("client_id", clientId);
      return new URL(`api/admin/feedback-csv?${p}`, INITIAL_BASE).toString();
    },
    /** Q4: preview correction patterns the worker would promote to rules. */
    async rulesPreview(threshold = 3, clientId?: string): Promise<{
      threshold: number;
      total: number;
      patterns: Array<{
        client_id: string | null;
        card_kind: string;
        field_path: string;
        old_value: string;
        new_value: string;
        occurrence_count: string;
        distinct_cards: string;
        first_seen: string;
        last_seen: string;
        promotable: boolean;
      }>;
    }> {
      const p = new URLSearchParams();
      p.set("threshold", String(threshold));
      if (clientId) p.set("client_id", clientId);
      return req<any>(`api/admin/rules?${p}`);
    },
    /** Q4: enqueue rules.rebuild — worker re-reads patterns and writes rules.jsonl. */
    async rulesRebuild(): Promise<{ enqueued: boolean; job_id: string | null }> {
      return req<any>("api/admin/rules/rebuild", { method: "POST" });
    },
    /** Stage A: upload PDF to OCR test pipeline. Returns job_id; client polls status. */
    async testOcrStart(file: File): Promise<{ test_id: string; job_id: string }> {
      const fd = new FormData();
      fd.append("pdf", file);
      return req<any>("api/admin/test-ocr/start", { method: "POST", body: fd });
    },
    /** Start from an existing pipeline card — no upload, sidecar loads PDF
     *  from /app/hub/data. Used by Werkstatt Per-Cell debug mode. */
    async testOcrStartFromCard(cardId: string, cardKind: "timesheet" | "expense"): Promise<{ test_id: string; job_id: string; source_card_id: string }> {
      const params = new URLSearchParams({ card_id: cardId, card_kind: cardKind });
      return req<any>(`api/admin/test-ocr/start?${params}`, { method: "POST" });
    },
    async testOcrListRuns(limit = 50, offset = 0, pdfSha256?: string): Promise<{
      runs: Array<{
        test_id: string;
        pdf_filename: string;
        pdf_sha256: string;
        created_at: string;
        pipeline_status: string;
        pipeline_error: string | null;
        strategy_used: string | null;
        form_type: string | null;
        rows_count: number | null;
        cols_count: number | null;
        n_cells_with_reads: number;
        n_gt: number;
      }>;
      limit: number;
      offset: number;
    }> {
      const p = new URLSearchParams({ limit: String(limit), offset: String(offset) });
      if (pdfSha256) p.set("pdf_sha256", pdfSha256);
      return req<any>(`api/admin/test-ocr/runs?${p}`);
    },
    async testOcrLoadRun(testId: string): Promise<any> {
      return req<any>(`api/admin/test-ocr/runs/${testId}`);
    },
    async testOcrUploadGt(testId: string, csv: File): Promise<{ gt_count: number; filename: string; warning?: string }> {
      const fd = new FormData();
      fd.append("csv", csv);
      return req<any>(`api/admin/test-ocr/${testId}/gt-csv`, { method: "POST", body: fd });
    },
    /** GT von einer existierenden Karte laden (statt CSV-Upload). (2026-05-21) */
    async testOcrGtFromCard(testId: string, sourceCardId: string, sourceKind: "timesheet" | "expense"): Promise<{ gt_count: number; source_card_id: string }> {
      return req<any>(`api/admin/test-ocr/${testId}/gt-from-card`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ source_card_id: sourceCardId, source_kind: sourceKind }),
      });
    },
    /** Test-Run abschließen + Metadaten persistieren (duration, models, totals, from_card). */
    async testOcrFinalize(testId: string, payload: {
      duration_s: number;
      models_used: string[];
      totals?: any;
      from_card_id?: string;
      gt_source_card_id?: string;
    }): Promise<{ ok: true; test_id: string; batch_run: any }> {
      return req<any>(`api/admin/test-ocr/${testId}/finalize`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(payload),
      });
    },
    /** Test-Runs für eine spezifische Karte (Werkstatt-Visibility). */
    async testOcrByCard(cardId: string, limit = 5): Promise<{
      card_id: string;
      runs: Array<{
        test_id: string;
        created_at: string;
        pdf_filename: string;
        pipeline_status: string;
        strategy_used: string | null;
        result: any;
        reads_count: number;
        gt_count: number;
      }>;
    }> {
      return req<any>(`api/admin/test-ocr/by-card/${encodeURIComponent(cardId)}?limit=${limit}`);
    },
    async testOcrDeleteRun(testId: string): Promise<{ deleted: number }> {
      return req<any>(`api/admin/test-ocr/runs/${testId}`, { method: "DELETE" });
    },
    async testOcrDeleteBatch(testIds: string[] | { all: true }): Promise<{ deleted: number; scope: string }> {
      const body = Array.isArray(testIds) ? { test_ids: testIds } : testIds;
      return req<any>("api/admin/test-ocr/runs/delete-batch", {
        method: "POST", body: JSON.stringify(body),
      });
    },
    async testOcrClearCell(testId: string, day: number, field: string): Promise<{ cell_key: string; cleared: boolean }> {
      return req<any>(`api/admin/test-ocr/${testId}/cell-reads`, {
        method: "DELETE", body: JSON.stringify({ day, field }),
      });
    },
    async testOcrStatus(jobId: string): Promise<{
      job_id: string;
      status: "queued" | "running" | "done" | "failed";
      percent: number;
      stage: string;
      message: string;
      stages_log: Array<{ stage: string; percent: number; ts: number; message: string }>;
      result: null | {
        strategy_used: string;
        rows_count: number;
        cols_count: number;
        fields_found: string[];
        anchor_truth: { anchors_found: number; days_detected: number[] } | null;
        image_w: number;
        image_h: number;
        png_url: string;
      };
      error: string | null;
      error_detail: any;
      form_info?: {
        stundenliste_hits: number; diaeten_hits: number;
        s_keys: string[]; d_keys: string[];
        preview_0deg?: string;
        rotations?: Record<string, string>;
        decision?: { form_type: string; rule: string };
      } | null;
      pdf_filename: string;
    }> {
      return req<any>(`api/admin/test-ocr/${jobId}`);
    },
    testOcrPngUrl(jobId: string): string {
      return new URL(`api/admin/test-ocr/${jobId}/annotated.png`, INITIAL_BASE).toString();
    },
    async testOcrModels(): Promise<{
      models: Array<{
        name: string; label: string; loaded: boolean;
        speed: string; approx_ms_per_cell: number;
        description: string;
        strategy?: string;
        strategy_description?: string;
      }>;
    }> {
      return req<any>("api/admin/test-ocr/models");
    },
    async testOcrCell(
      jobId: string,
      day: number,
      field: "von1" | "bis1" | "von2" | "bis2",
      models: string[],
    ): Promise<{
      cell_key: string;
      result?: any;
      results?: any[];
    }> {
      return req<any>(`api/admin/test-ocr/${jobId}/ocr-cell`, {
        method: "POST",
        body: JSON.stringify({ day, field, models }),
      });
    },
    async testOcrReads(jobId: string): Promise<{
      job_id: string;
      cell_bboxes: Record<string, {
        day: number; field: string;
        x1: number; y1: number; x2: number; y2: number;
        leftPct: number; topPct: number; rightPct: number; bottomPct: number;
      }>;
      reads: Record<string, Record<string, {
        value: string | null; confidence: number; latency_ms: number;
        raw_output: string | null; error: string | null;
      }>>;
      gt: Record<string, string>;
      image_w: number;
      image_h: number;
    }> {
      return req<any>(`api/admin/test-ocr/${jobId}/reads`);
    },
  },

  // Export (Phase 5)
  export: {
    async status(month: string, clientId?: string): Promise<{ total: number; ready_for_export: number; export_ready: boolean; counts: Record<string, number> }> {
      const params = new URLSearchParams();
      if (clientId) params.set("client_id", clientId);
      return req(`api/monthly-batches/${month}/status?${params}`);
    },
    async downloadUrl(month: string, clientId?: string, opts?: { cardId?: string; includeExported?: boolean; auditOnly?: boolean }): Promise<string> {
      const params = new URLSearchParams();
      if (clientId) params.set("client_id", clientId);
      if (opts?.cardId) params.set("card_id", opts.cardId);
      if (opts?.includeExported) params.set("include_exported", "true");
      if (opts?.auditOnly) params.set("audit_only", "true");
      return new URL(`api/monthly-batches/${month}?${params}`, INITIAL_BASE).toString();
    },
    /** Triggers POST + handles browser download. Returns nothing — page navigates. */
    async download(month: string, clientId?: string, opts?: { cardId?: string; includeExported?: boolean; auditOnly?: boolean }): Promise<void> {
      const url = await this.downloadUrl(month, clientId, opts);
      const res = await fetch(url, { method: "POST", credentials: "same-origin" });
      if (!res.ok) {
        const txt = await res.text().catch(() => "");
        throw new Error(`Export fehlgeschlagen: ${res.status} ${txt}`);
      }
      const blob = await res.blob();
      const blobUrl = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = blobUrl;
      a.download = `sibit-rzl-${clientId ?? "default"}-${month}${opts?.cardId ? `-${opts.cardId.slice(0, 8)}` : ""}.csv`;
      a.click();
      URL.revokeObjectURL(blobUrl);
    },
    /** Monats-Neuberechnung (Cross-Month): alle calculated+exported Karten
     *  des Monats → validated + Calc-Job. Nötig wenn der VORMONAT nachträglich
     *  fertig wurde (geteilte-Woche-Regel zieht Vormonats-Tage hinzu). */
    async recalculate(month: string, clientId?: string): Promise<{ requeued_timesheets: number; requeued_expenses: number }> {
      const params = new URLSearchParams();
      if (clientId) params.set("client_id", clientId);
      return req(`api/monthly-batches/${month}/recalculate?${params}`, { method: "POST" });
    },
  },

  // Test-protocols (admin) — on-demand validator runs persisted in
  // test_run_protocols. The full-run buttons are wired directly in
  // AdminTestProtocolsPage; this namespace covers per-card scoped runs.
  testProtocols: {
    async runHighlightForCard(cardId: string): Promise<RunHighlightForCardResponse> {
      return req<RunHighlightForCardResponse>(
        `api/admin/test-protocols/run-highlight/${encodeURIComponent(cardId)}`,
        { method: "POST" },
      );
    },
    /** Letzter persistierter Single-Card-Lauf. Null wenn die Karte noch nie geprüft wurde. */
    async getLatestHighlightForCard(cardId: string): Promise<RunHighlightForCardResponse | null> {
      try {
        return await req<RunHighlightForCardResponse>(
          `api/admin/test-protocols/run-highlight/${encodeURIComponent(cardId)}/latest`,
        );
      } catch (e: any) {
        // 404 = noch nie gelaufen → idle. Andere Errors propagieren.
        if (e?.status === 404) return null;
        throw e;
      }
    },
    /** Pre-OCR Rotation-Test via Tesseract OSD. Kind-agnostisch — funktioniert
     *  für Stundenliste und Diäten. Persistenz wie HighlightForCard, eigener
     *  notes-Discriminator. */
    async runRotationForCard(cardId: string): Promise<RunRotationForCardResponse> {
      return req<RunRotationForCardResponse>(
        `api/admin/test-protocols/run-rotation/${encodeURIComponent(cardId)}`,
        { method: "POST" },
      );
    },
    async getLatestRotationForCard(cardId: string): Promise<RunRotationForCardResponse | null> {
      try {
        return await req<RunRotationForCardResponse>(
          `api/admin/test-protocols/run-rotation/${encodeURIComponent(cardId)}/latest`,
        );
      } catch (e: any) {
        if (e?.status === 404) return null;
        throw e;
      }
    },
  },

  // Feedback-Endpoints entfernt 2026-05-19 (Migration 034). Korrekturen
  // werden jetzt server-side automatisch in data.days[N].field.corrections[]
  // appended, sobald PATCH /api/timesheets/:id einen Cell-Wert ändert —
  // siehe hub/src/repos/timesheet.ts:mergeCardData.

  // Batch transitions (Phase 3)
  batch: {
    async validate(kind: "timesheet" | "expense", ids: string[]): Promise<{ validated: number; errors: any[] }> {
      return req<any>("api/cards/batch-validate", {
        method: "POST",
        body: JSON.stringify({ kind, ids }),
      });
    },
  },

  // User preferences (Decision 9)
  preferences: {
    async get(): Promise<{ diaet_merge_mode?: "auto_with_banner" | "confirm" }> {
      const body = await req<{ preferences: any }>("api/me/preferences");
      return body.preferences ?? {};
    },
    async patch(patch: { diaet_merge_mode?: "auto_with_banner" | "confirm" }): Promise<any> {
      const body = await req<{ preferences: any }>("api/me/preferences", {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.preferences;
    },
  },

  // Uploads (Phase 2 multi-page PDF flow)
  uploads: {
    async pdf(
      file: File,
      month: string,
      clientId?: string,
      docType?: string,
    ): Promise<{
      upload_id: string;
      page_count_in: number;
      summary: { timesheets: number; expenses: number; unknown: number; blank: number };
      cards: Array<{ kind: string; id: string; source_page_index: number }>;
      // 409 response when duplicate — bubbled as thrown Error by req() above
    }> {
      const fd = new FormData();
      fd.set("pdf", file);
      fd.set("month", month);
      if (clientId) fd.set("client_id", clientId);
      // Manuelle Dokumenttyp-Wahl (mandant-spezifisch) → autoritatives Routing im Hub.
      if (docType) fd.set("doc_type", docType);
      return req("api/uploads/pdf", { method: "POST", body: fd });
    },
    /** Excel-Upload (.xlsx) — typ-agnostisch: der Hub splittet die Datei je
     *  (Mitarbeiter × doc_type) in Zulagen-/Reisekosten-Karten (Art→Matrix→
     *  doc_type). KEIN doc_type-Parameter — die Typisierung ist datengetrieben. */
    async xlsx(
      file: File,
      month: string,
      clientId?: string,
    ): Promise<{
      imported_rows: number;
      employees_matched: number;
      karten: Array<{ id: string; doc_type: string; mitarbeiter_id: string; rows: number }>;
      karten_count: number;
      karten_id: string | null;
      summen_preview: unknown[];
      nicht_exportierbar: string[];
      uebersprungene_fusszeilen?: unknown;
    }> {
      const fd = new FormData();
      fd.set("xlsx", file);
      fd.set("month", month);
      if (clientId) fd.set("client_id", clientId);
      return req("api/uploads/xlsx", { method: "POST", body: fd });
    },
    /** Per-Mandant Dokumenttypen + Auto-Erkennungs-Flag für den Upload-Selektor.
     *  #562-Rework: „Automatisch erkennen" ist ein Lieferversprechen — die Option
     *  erscheint nur, wenn der Mandant sie per Regelwerk aktiviert hat. */
    async docTypes(clientId: string): Promise<{
      types: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
      autoDetect: boolean;
    }> {
      const body = await req<{
        doc_types: Array<{ id: string; label: string; routesTo: "timesheet" | "expense" }>;
        upload_auto_detect?: boolean;
      }>(`api/clients/${encodeURIComponent(clientId)}/doc-types`);
      return { types: body.doc_types, autoDetect: body.upload_auto_detect === true };
    },
  },

  // Expense merge-banner + unmerge
  expenseMerges: {
    async since(isoTs: string, clientId?: string): Promise<Array<{ merged_card_id: string; target_card_id: string; month: string; first_name: string | null; last_name: string | null }>> {
      const params = new URLSearchParams({ merged_since: isoTs });
      if (clientId) params.set("client_id", clientId);
      const body = await req<{ merges: any[] }>(`api/expenses?${params}`);
      return body.merges;
    },
    async unmerge(mergedCardId: string): Promise<void> {
      await req<any>(`api/expenses/${mergedCardId}/unmerge`, { method: "POST" });
    },
  },

  // Cards (kanban polling)
  cards: {
    /** Twin-Simulation: schickt die Karte (seiteneffektfrei) durch die Räume und
     *  liefert die Mappe (Artefakte) + pro Raum needs/produces/status + Hängepunkt. */
    async simulate(kind: "timesheet" | "expense", id: string): Promise<{
      card_id: string; kind: string; twin_version: string; current_version: string;
      flows_through: boolean; hangs_at: string | null; needs_lazy_migration: boolean;
      dossier: Array<{ id: string; label: string; present: boolean; version: string | null; stale: boolean }>;
      gates: Array<{ gate: string; label: string; needs: string[]; produces: string;
        status: "done" | "active" | "error" | "not-reached"; version_ok: boolean;
        needs_migration: boolean; reason: string; missing?: string[] }>;
    }> {
      return req(`api/cards/${kind}/${id}/simulate`);
    },
    /** Diäten OCR-Raum Drill-in: führt die ECHTE Extraktion (sidecar /ocr/expense)
     *  gegen das Karten-PDF aus, LIVE, ohne die Karte zu verändern (committing:false).
     *  Liefert {entries, meta} zum Vergleich gegen die committeten data.entries. */
    async debugExtract(id: string): Promise<{
      card_id: string; committing: false; elapsed_ms: number;
      entries: any[]; meta: any | null; header: any | null;
    }> {
      return req(`api/cards/expense/${id}/debug-extract`, { method: "POST" });
    },
    /** DESTRUKTIV (nur Dev/Demo-Karten): setzt die Karte zurück „als ob frisch
     *  hochgeladen" (state=ocr_pending, data geleert) und enqueued frische OCR.
     *  Danach durchläuft der Twin die Pipeline neu — via simulate() beobachtbar. */
    async reset(kind: "timesheet" | "expense", id: string): Promise<{
      ok: boolean; card_id: string; kind: string; new_state: string; job_id: string | null; reset: boolean;
    }> {
      return req(`api/cards/${kind}/${id}/reset`, { method: "POST" });
    },
    async list(
      month: string | undefined,
      since: string,
      clientId?: string,
      type?: CardTypeFilter,
      opts?: { archivedOnly?: boolean },
    ): Promise<CardsResponse> {
      const params = new URLSearchParams();
      if (month) params.set("month", month);
      params.set("since", since);
      if (clientId) params.set("client_id", clientId);
      if (type && type !== "all") params.set("type", type);
      if (opts?.archivedOnly) params.set("archived_only", "true");
      return req<CardsResponse>(`api/cards?${params}`);
    },
    /** Aggregat-Counter (UI-Tab-unabhängig). Single source of truth für
     *  Kanban-Tab-Badges + Werkstatt-Bestand-View. Server filtert nicht
     *  nach type — sondern liefert IMMER alle vier Buckets, damit die
     *  Counter nicht je Tab-Wahl driften. */
    async counts(month?: string, clientId?: string): Promise<{
      timesheet: number;
      expense: number;
      unknown: number;
      conflict: number;
      archived: number;
      total: number;
      /** Zähler pro doc_type (card #523) — zugleich die DISTINCT-doc_type-Menge
       *  der aktiven Karten, aus der die historischen Filter-Tabs abgeleitet
       *  werden. Schlüssel = doc_type-id (data.manual_doc_type ?? meta.doc_type). */
      by_doc_type: Record<string, number>;
    }> {
      const params = new URLSearchParams();
      if (month) params.set("month", month);
      if (clientId) params.set("client_id", clientId);
      return req(`api/cards/counts${params.toString() ? "?" + params : ""}`);
    },
    /** Kanonischer Card-Lookup: probiert beide Tabellen (timesheets + expenses)
     *  und gibt die Karte mit `kind`-Discriminator zurück. Werkstatt-Page nutzt
     *  das statt api.timesheets.get — sonst 404 bei expense-IDs. */
    async byId(id: string): Promise<Card> {
      const body = await req<{ card: Card }>(`api/cards/by-id/${id}`);
      return body.card;
    },
    /** Manueller OCR-Restart für stuck cards. Hub akzeptiert nur wenn
     *  stuck_ocr=true (state='ocr_running' und updated_at >15min alt) — sonst
     *  409 not_stuck. Setzt state zurück auf 'ocr_pending' und enqueued einen
     *  neuen card.ocr Job. */
    async restartOcr(id: string): Promise<{ ok: true; card_id: string; kind: string; new_state: string; job_id: string | null; previous_stuck_minutes: number }> {
      return req(`api/cards/${encodeURIComponent(id)}/restart-ocr`, { method: "POST" });
    },
    /** Glass-Box-Diagnose für stuck OCR: aggregiert pg-boss-Job + Sidecar-Status
     *  + Hypothesen-Klassifikation. Beantwortet "läuft OCR noch?" vs "echter
     *  Stillstand?" — siehe hub/routes/cards.ts:stuck-diagnosis. */
    async stuckDiagnosis(id: string): Promise<StuckDiagnosis> {
      return req(`api/cards/${encodeURIComponent(id)}/stuck-diagnosis`);
    },
    /** Zurück nach ocr_complete aus {calculated, exported}. 409 wenn state
     *  anders. Berechnung wird beim nächsten /calculate überschrieben,
     *  Excel-Datei aus Export bleibt im FS — re-Export überschreibt sie. */
    async revertToOcrComplete(id: string): Promise<{ ok: true; card_id: string; kind: string; previous_state: string; new_state: string }> {
      return req(`api/cards/${encodeURIComponent(id)}/revert-to-ocr-complete`, { method: "POST" });
    },
    /** #506: Freigabe einer „Prüfen"-Karte (pending_review → ocr_complete),
     *  needs_review_reason geräumt, OCR-Werte unangetastet. 409 wenn nicht
     *  in pending_review. */
    async approveReview(kind: "timesheet" | "expense", id: string, reason?: string): Promise<{ ok: true; state: string; kind: string }> {
      return req(`api/cards/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/approve-review`, {
        method: "POST",
        body: JSON.stringify(reason ? { reason } : {}),
      });
    },
    /** #563: Karten-Typ umhängen (Zulage↔Reisekosten, oder Sonstige→bekannter Typ).
     *  Setzt manual_doc_type = die doc_type-Identität → richtiger Tab. */
    async setDocType(kind: "timesheet" | "expense", id: string, docType: string): Promise<{ ok: true; doc_type: string; kind: string }> {
      return req(`api/cards/${encodeURIComponent(kind)}/${encodeURIComponent(id)}/set-doc-type`, {
        method: "POST",
        body: JSON.stringify({ doc_type: docType }),
      });
    },
  },

  // Image-Preprocessing-Lab (Phase 1 Step 3, eingeführt 2026-05-21)
  preprocess: {
    async list(cardId: string): Promise<{
      card_id: string;
      kind: "timesheet" | "expense";
      pdf_path: string;
      active_variant: string;
      variants: PreprocessVariant[];
    }> {
      return req(`api/preprocess/${encodeURIComponent(cardId)}`);
    },
    async baseline(cardId: string): Promise<{ card_id: string; baseline: AnchorMeasure }> {
      return req(`api/preprocess/${encodeURIComponent(cardId)}/baseline`, { method: "POST" });
    },
    async test(cardId: string, type: "clahe" | "sauvola" | "hough_redraw", params?: Record<string, number | string | boolean>): Promise<{
      card_id: string; variant: PreprocessVariant; cached: boolean;
    }> {
      return req(`api/preprocess/${encodeURIComponent(cardId)}/test`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ type, params: params ?? {} }),
      });
    },
    async activate(cardId: string, variantId: string): Promise<{ card_id: string; active_variant: string }> {
      return req(`api/preprocess/${encodeURIComponent(cardId)}/activate`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ variant_id: variantId }),
      });
    },
    imageUrl(cardId: string, variant: string = "active"): string {
      return `api/preprocess/${encodeURIComponent(cardId)}/image?variant=${encodeURIComponent(variant)}`;
    },
  },

  // Timesheets
  timesheets: {
    async get(id: string, signal?: AbortSignal): Promise<Timesheet> {
      const body = await req<unknown>(`api/timesheets/${id}`, { signal });
      // Runtime contract validation. Throws on shape violation — turns
      // silent BE/FE drift into a loud failure that surfaces during dev
      // OR in user-error UI rather than corrupting downstream state.
      // Pattern source: feedback_manuell_reaktiv_pattern.md.
      const { parseTimesheetEnvelope } = await import("./schema");
      const parsed = parseTimesheetEnvelope(body);
      return parsed.timesheet as unknown as Timesheet;
    },
    pdfUrl(id: string): string {
      return new URL(`api/timesheets/${id}/pdf`, INITIAL_BASE).toString();
    },
    /** Liste der verfügbaren Cell-Image-Keys ("d01_von1"…). Compare-View
     *  rendert img nur für vorhandene Keys, sonst 404-Sturm bei Karten wo
     *  z.B. die symb-Spalte vom Grid-Detector verfehlt wurde. `signal` für
     *  AbortController-Cleanup beim Karten-Wechsel.
     *  `meta.alignment.aligned`: server-computed Anchor-Truth-Validation —
     *  false = Grid-Drift erkannt (silent data corruption Gefahr), Compare-View
     *  MUSS hart blocken. */
    async cellImageKeys(id: string, signal?: AbortSignal): Promise<{
      keys: string[];
      meta?: {
        anchor_truth?: any;
        form_type?: string | null;
        alignment?: {
          aligned: boolean | null;
          delta_top_pct: number | null;
          delta_bot_pct: number | null;
          threshold: number;
        };
      };
    }> {
      return req(`api/timesheets/${encodeURIComponent(id)}/cell-image-keys`, { signal });
    },
    async upload(month: string, file: File, clientId?: string): Promise<Timesheet> {
      const fd = new FormData();
      fd.set("month", month);
      fd.set("pdf", file);
      if (clientId) fd.set("client_id", clientId);
      const body = await req<{ timesheet: Timesheet }>("api/timesheets/upload", {
        method: "POST",
        body: fd,
      });
      return body.timesheet;
    },
    async patch(id: string, patch: { employee_id?: string | null; data?: Timesheet["data"] }): Promise<Timesheet> {
      const body = await req<{ timesheet: Timesheet }>(`api/timesheets/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.timesheet;
    },
    async validate(id: string): Promise<Timesheet> {
      const body = await req<{ timesheet: Timesheet }>(`api/timesheets/${id}/validate`, {
        method: "POST",
      });
      return body.timesheet;
    },
    /** Manual-mode: trigger OCR on a card stuck in state=uploaded. */
    async startOcr(id: string): Promise<Timesheet> {
      const body = await req<{ timesheet: Timesheet }>(`api/timesheets/${id}/start-ocr`, {
        method: "POST",
      });
      return body.timesheet;
    },
    /** Resolve a pending_conflict card. action ∈ {overwrite, discard, reassign}. */
    async resolveConflict(
      id: string,
      action: "overwrite" | "discard" | "reassign",
      employeeId?: string,
    ): Promise<void> {
      await req<unknown>(`api/timesheets/${id}/resolve-conflict`, {
        method: "POST",
        body: JSON.stringify({ action, employee_id: employeeId }),
      });
    },
    async delete(id: string): Promise<void> {
      await req<{ ok: true }>(`api/timesheets/${id}`, { method: "DELETE" });
    },
  },

  // Employees
  employees: {
    async list(clientId?: string, signal?: AbortSignal): Promise<Employee[]> {
      const params = new URLSearchParams();
      if (clientId) params.set("client_id", clientId);
      const qs = params.toString();
      const body = await req<{ employees: Employee[] }>(
        `api/employees${qs ? `?${qs}` : ""}`,
        { signal },
      );
      return body.employees;
    },
    async create(input: {
      client_id?: string;
      dienstnehmernummer: string;
      first_name: string;
      last_name: string;
    }): Promise<Employee> {
      const body = await req<{ employee: Employee }>("api/employees", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return body.employee;
    },
    async import(
      file: File,
      clientId?: string,
    ): Promise<{ imported: number; errors: any[] }> {
      const fd = new FormData();
      fd.set("csv", file);
      if (clientId) fd.set("client_id", clientId);
      return req("api/employees/import", { method: "POST", body: fd });
    },
    async patch(id: string, patch: Partial<Employee>): Promise<Employee> {
      const body = await req<{ employee: Employee }>(`api/employees/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.employee;
    },
    async changeWeeklyThreshold(id: string, clientId: string, hours: number | null, fromMonth: string | null): Promise<void> {
      await req(`api/employees/${id}/weekly-threshold`, { method: "PATCH",
        body: JSON.stringify({ client_id: clientId, threshold_h: hours, from_month: fromMonth }) });
    },
  },

  // KV Configs
  kvConfigs: {
    async list(clientId?: string): Promise<KvConfig[]> {
      const params = new URLSearchParams();
      if (clientId) params.set("client_id", clientId);
      const qs = params.toString();
      const body = await req<{ kv_configs: KvConfig[] }>(
        `api/kv-configs${qs ? `?${qs}` : ""}`,
      );
      return body.kv_configs;
    },
    async create(input: Partial<KvConfig> & { client_id?: string }): Promise<KvConfig> {
      const body = await req<{ kv_config: KvConfig }>("api/kv-configs", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return body.kv_config;
    },
    async patch(id: string, patch: Partial<KvConfig>): Promise<KvConfig> {
      const body = await req<{ kv_config: KvConfig }>(`api/kv-configs/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.kv_config;
    },
    async setDefault(id: string): Promise<KvConfig> {
      const body = await req<{ kv_config: KvConfig }>(`api/kv-configs/${id}/set-default`, {
        method: "POST",
      });
      return body.kv_config;
    },
  },

  // Clients
  clients: {
    async list(opts: { includeArchived?: boolean } = {}): Promise<Client[]> {
      const qs = opts.includeArchived ? "?include_archived=true" : "";
      const body = await req<{ clients: Client[] }>(`api/clients${qs}`);
      return body.clients;
    },
    async create(input: {
      id: string;
      name: string;
      contact_email?: string | null;
      contact_name?: string | null;
      notes?: string | null;
    }): Promise<Client> {
      const body = await req<{ client: Client }>("api/clients", {
        method: "POST",
        body: JSON.stringify(input),
      });
      return body.client;
    },
    async patch(id: string, patch: Partial<Client>): Promise<Client> {
      const body = await req<{ client: Client }>(`api/clients/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.client;
    },
  },

  // Client KV defaults (temporal)
  clientKvDefaults: {
    async list(clientId: string): Promise<ClientKvDefault[]> {
      const params = new URLSearchParams({ client_id: clientId });
      const body = await req<{ defaults: ClientKvDefault[] }>(
        `api/client-kv-defaults?${params}`,
      );
      return body.defaults;
    },
    async add(input: {
      client_id: string;
      kv_config_id: string;
      valid_from: string;
      valid_until?: string | null;
      note?: string | null;
    }): Promise<ClientKvDefault> {
      const body = await req<{ default: ClientKvDefault }>(
        "api/client-kv-defaults",
        { method: "POST", body: JSON.stringify(input) },
      );
      return body.default;
    },
    async delete(id: string): Promise<void> {
      await req<{ ok: true }>(`api/client-kv-defaults/${id}`, {
        method: "DELETE",
      });
    },
  },

  // Employee KV assignments (temporal override)
  employeeKvAssignments: {
    async list(employeeId: string): Promise<EmployeeKvAssignment[]> {
      const params = new URLSearchParams({ employee_id: employeeId });
      const body = await req<{ assignments: EmployeeKvAssignment[] }>(
        `api/employee-kv-assignments?${params}`,
      );
      return body.assignments;
    },
    async resolve(
      employeeId: string,
      date: string,
    ): Promise<{ kv_config_id: string | null }> {
      const params = new URLSearchParams({ employee_id: employeeId, date });
      return req<{ kv_config_id: string | null }>(
        `api/employee-kv-assignments/resolve?${params}`,
      );
    },
    async add(input: {
      employee_id: string;
      kv_config_id: string;
      valid_from: string;
      valid_until?: string | null;
      note?: string | null;
    }): Promise<EmployeeKvAssignment> {
      const body = await req<{ assignment: EmployeeKvAssignment }>(
        "api/employee-kv-assignments",
        { method: "POST", body: JSON.stringify(input) },
      );
      return body.assignment;
    },
    async delete(id: string): Promise<void> {
      await req<{ ok: true }>(`api/employee-kv-assignments/${id}`, {
        method: "DELETE",
      });
    },
  },

  // Users (admin only)
  users: {
    async list(): Promise<User[]> {
      const body = await req<{ users: User[] }>("api/users");
      return body.users;
    },
    async invite(email: string, display_name: string, role?: UserRole): Promise<{ user: User; temp_password: string }> {
      return req("api/users/invite", {
        method: "POST",
        body: JSON.stringify({ email, display_name, role }),
      });
    },
    async patch(
      id: string,
      patch: { role?: UserRole; display_name?: string; email?: string },
    ): Promise<User> {
      const body = await req<{ user: User }>(`api/users/${id}`, {
        method: "PATCH",
        body: JSON.stringify(patch),
      });
      return body.user;
    },
    async delete(id: string): Promise<void> {
      await req<{ ok: true }>(`api/users/${id}`, { method: "DELETE" });
    },
  },

  // System: Version + Betriebsmodus (P3 Live-Modus, developer-only POST)
  system: {
    async version(): Promise<{ version: string; commit: string | null; branch: string | null; builtAt: string | null }> {
      return req("api/system/version");
    },
    async mode(): Promise<{ mode: "stable" | "live"; transition: string; locked: boolean }> {
      return req("api/system/mode");
    },
    async setMode(target: "stable" | "live"): Promise<{ requested?: string; mode: string; transition: string; locked: boolean; changed?: boolean }> {
      return req("api/system/mode", { method: "POST", body: JSON.stringify({ mode: target }) });
    },
    async updateStatus(): Promise<{
      current: string;
      latest: string;
      updateAvailable: boolean;
      latestChangelog: string | null;
      releases: Array<{ tag: string; date?: string; changelog?: string }>;
    }> {
      return req("api/system/update-status");
    },
    async install(tag?: string): Promise<{ runId: number | null; target: string; from: string }> {
      return req("api/system/install", { method: "POST", body: JSON.stringify(tag ? { tag } : {}) });
    },
    async installStatus(): Promise<{
      run: null | {
        id: number; target_tag: string; from_version: string; requested_by: string;
        status: string; steps: Array<{ ts: string; step: string; detail: string }>;
        error: string | null; started_at: string; finished_at: string | null;
      };
      transition: string;
    }> {
      return req("api/system/install-status");
    },
  },
};
